import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { formatMoneyCurrency } from '@share/utils';

import { ItemTypeEnum } from '@share/common-types';

import { DetailPriceRow } from './components';
import type { DetailPriceCardProps } from './types';
import { getAverageRetailDescription } from './utils';

import styles from './styles.module.scss';

function itemTypeMessageId(type: ItemTypeEnum): string {
  if (type === ItemTypeEnum.Hotel) {
    return 'certificates.itemType.hotel';
  }
  if (type === ItemTypeEnum.Cruise) {
    return 'certificates.itemType.cruise';
  }
  if (type === ItemTypeEnum.Condo) {
    return 'certificates.itemType.condo';
  }
  return 'certificates.itemType.hotel';
}

export const DetailPriceCard: React.FC<DetailPriceCardProps> = ({
  itemTemplates,
  totalPrice,
  currency,
  averageRetailPrice,
  savings,
}) => {
  const intl = useIntl();
  const formatMoney = useMemo(() => formatMoneyCurrency(currency), [currency]);
  const averageRetailDescription = useMemo(
    () => getAverageRetailDescription(intl, itemTemplates ?? []),
    [intl, itemTemplates],
  );
  const hasComparison = (averageRetailPrice ?? 0) > 0 || (savings ?? 0) !== 0;

  return (
    <aside className={styles.rightColumn} aria-labelledby="price-details-heading">
      <div className={styles.priceCard}>
        <h2 id="price-details-heading" className={styles.sectionTitle}>
          {intl.formatMessage({ id: 'certificates.detail.priceDetails' })}
        </h2>

        <ul className={styles.itemList}>
          {itemTemplates?.map((item) => (
            <li key={item.id}>
              <DetailPriceRow
                className={styles.lineItem}
                label={intl.formatMessage(
                  { id: 'certificates.detail.priceLine' },
                  {
                    type: intl.formatMessage({ id: itemTypeMessageId(item.type) }),
                    nights: item.nights,
                  },
                )}
                value={formatMoney.format(item.publicPrice ?? 0)}
              />
            </li>
          ))}
        </ul>

        <DetailPriceRow
          className={styles.totalRow}
          label={
            <>
              <span className={styles.totalLabel}>
                {intl.formatMessage({ id: 'certificates.detail.totalPaid' })}
              </span>
              <p className={styles.taxesNote}>
                {intl.formatMessage({ id: 'certificates.detail.includingTaxes' })}
              </p>
            </>
          }
          value={<span className={styles.totalValue}>{formatMoney.format(totalPrice ?? 0)}</span>}
        />

        {hasComparison && (
          <>
            <hr className={styles.sectionDivider} />
            <div className={styles.comparisonSection}>
              {(averageRetailPrice ?? 0) > 0 && (
                <DetailPriceRow
                  className={styles.comparisonRow}
                  label={
                    <>
                      <span className={styles.comparisonTitle}>
                        {intl.formatMessage({ id: 'certificates.detail.averageRetail' })}
                      </span>
                      {averageRetailDescription ? (
                        <p className={styles.comparisonSubtext}>{averageRetailDescription}</p>
                      ) : null}
                    </>
                  }
                  value={
                    <span className={styles.retailValue}>
                      {formatMoney.format(averageRetailPrice ?? 0)}
                    </span>
                  }
                />
              )}
              {(savings ?? 0) !== 0 && (
                <DetailPriceRow
                  className={styles.comparisonRow}
                  label={
                    <span className={styles.savingsLabel}>
                      {intl.formatMessage({ id: 'certificates.detail.estimatedSavings' })}
                    </span>
                  }
                  value={
                    <span className={styles.savingsValue}>
                      {'- '}
                      {formatMoney.format(Math.abs(savings ?? 0))}
                    </span>
                  }
                />
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
